// Spreadsheet.js
import React, { useState, useEffect, useRef, useReducer } from 'react';
import { ResizableBox } from 'react-resizable';
import 'react-resizable/css/styles.css'; // Import styles for react-resizable
import Papa from "papaparse";
const Spreadsheet = () => {
  // Initialize data with 20 rows and 10 columns
  const initialRows = 20;
  const initialCols = 10;

  // State for cell data
  const [data, setData] = useState(() => {
    return Array.from({ length: initialRows }, () =>
      Array.from({ length: initialCols }, () => ({
        value: '',
        readOnly: false,
      }))
    );
  });

  // State for selection
  const [selection, setSelection] = useState({
    start: null,
    end: null,
  });

  // State for active cell (the cell that is focused)
  const [activeCell, setActiveCell] = useState(null);

  // State for editing cell
  const [editingCell, setEditingCell] = useState(null);

  // State for columns width
  const [colWidths, setColWidths] = useState(() => {
    return Array.from({ length: initialCols }, () => 100); // default width 100px
  });

  // State for rows height
  const [rowHeights, setRowHeights] = useState(() => {
    return Array.from({ length: initialRows }, () => 30); // default height 30px
  });

  // Refs
  const cellRefs = useRef({});

  // Undo/Redo functionality
  const historyReducer = (state, action) => {
    const { past, present, future } = state;
    switch (action.type) {
      case 'SET_DATA':
        return {
          past: [...past, present],
          present: action.data,
          future: [],
        };
      case 'UNDO':
        if (past.length === 0) return state;
        const previous = past[past.length - 1];
        return {
          past: past.slice(0, past.length - 1),
          present: previous,
          future: [present, ...future],
        };
      case 'REDO':
        if (future.length === 0) return state;
        const next = future[0];
        return {
          past: [...past, present],
          present: next,
          future: future.slice(1),
        };
      default:
        return state;
    }
  };

  const [historyState, dispatchHistory] = useReducer(historyReducer, {
    past: [],
    present: data,
    future: [],
  });

  const { present: presentData } = historyState;

  // Synchronize data with presentData
  useEffect(() => {
    setData(presentData);
  }, [presentData]);

  // Handle cell value change
  const handleChange = (e, rowIndex, colIndex) => {
    const newValue = e.target.value;

    const newData = data.map((row, r) =>
      row.map((cell, c) => {
        if (r === rowIndex && c === colIndex) {
          return { ...cell, value: newValue };
        } else {
          return cell;
        }
      })
    );

    dispatchHistory({ type: 'SET_DATA', data: newData });

    // Data validation example: you can add validation logic here
    // For example, if the cell should be numeric, you can check and set an error state
  };

  // Keyboard navigation
  const handleKeyDown = (e, rowIndex, colIndex) => {
    if (e.ctrlKey || e.metaKey) {
      // Handle clipboard operations
      if (e.key.toLowerCase() === 'c') {
        e.preventDefault();
        handleCopy();
      } else if (e.key.toLowerCase() === 'x') {
        e.preventDefault();
        handleCut();
      } else if (e.key.toLowerCase() === 'v') {
        e.preventDefault();
        handlePaste();
      } else if (e.key === 'z') {
        e.preventDefault();
        undo();
      } else if (e.key === 'y') {
        e.preventDefault();
        redo();
      }
    } else {
      let newRowIndex = rowIndex;
      let newColIndex = colIndex;
      switch (e.key) {
        case 'ArrowUp':
          newRowIndex = Math.max(rowIndex - 1, 0);
          break;
        case 'ArrowDown':
          newRowIndex = Math.min(rowIndex + 1, data.length - 1);
          break;
        case 'ArrowLeft':
          newColIndex = Math.max(colIndex - 1, 0);
          break;
        case 'ArrowRight':
          newColIndex = Math.min(colIndex + 1, data[0].length - 1);
          break;
        case 'Enter':
          if (editingCell && editingCell.row === rowIndex && editingCell.col === colIndex) {
            // Exit edit mode
            setEditingCell(null);
          } else {
            // Enter edit mode
            setEditingCell({ row: rowIndex, col: colIndex });
            // Focus the input when entering edit mode
            const cellKey = `${rowIndex}-${colIndex}`;
            const nextCell = cellRefs.current[cellKey];
            if (nextCell) {
              nextCell.focus();
            }
          }
          break;
        case 'Tab':
          e.preventDefault();
          newColIndex = (colIndex + 1) % data[0].length;
          if (newColIndex === 0) {
            newRowIndex = (rowIndex + 1) % data.length;
          }
          break;
        case 'Escape':
          if (editingCell) {
            // Exit edit mode without saving
            setEditingCell(null);
          }
          break;
        case 'Delete':
          // Delete contents of selected cells
          handleDelete();
          break;
        default:
          break;
      }

      // Set active cell
      setActiveCell({ row: newRowIndex, col: newColIndex });

      // Focus the new cell
      const cellKey = `${newRowIndex}-${newColIndex}`;
      const nextCell = cellRefs.current[cellKey];
      if (nextCell) {
        nextCell.focus();
      }
    }
  };

  // Handle cell click
  const handleCellClick = (rowIndex, colIndex, e) => {
    if (e.shiftKey) {
      // Multi-cell selection with Shift+Click
      if (selection.start) {
        setSelection((prev) => ({
          ...prev,
          end: { row: rowIndex, col: colIndex },
        }));
      } else {
        setSelection({
          start: { row: activeCell.row, col: activeCell.col },
          end: { row: rowIndex, col: colIndex },
        });
      }
    } else {
      // Set active cell
      setActiveCell({ row: rowIndex, col: colIndex });
      setSelection({
        start: { row: rowIndex, col: colIndex },
        end: { row: rowIndex, col: colIndex },
      });
    }
  };

  // Handle double click to enter edit mode
  const handleDoubleClick = (rowIndex, colIndex) => {
    setEditingCell({ row: rowIndex, col: colIndex });
  };

  // Determine if a cell is selected
  const isCellSelected = (rowIndex, colIndex) => {
    if (!selection.start || !selection.end) return false;
    const startRow = Math.min(selection.start.row, selection.end.row);
    const endRow = Math.max(selection.start.row, selection.end.row);
    const startCol = Math.min(selection.start.col, selection.end.col);
    const endCol = Math.max(selection.start.col, selection.end.col);
    return rowIndex >= startRow && rowIndex <= endRow && colIndex >= startCol && colIndex <= endCol;
  };

  // Handle copy
  const handleCopy = () => {
    if (!selection.start || !selection.end) return;
    const startRow = Math.min(selection.start.row, selection.end.row);
    const endRow = Math.max(selection.start.row, selection.end.row);
    const startCol = Math.min(selection.start.col, selection.end.col);
    const endCol = Math.max(selection.start.col, selection.end.col);

    let copyData = '';
    for (let r = startRow; r <= endRow; r++) {
      let rowValues = [];
      for (let c = startCol; c <= endCol; c++) {
        rowValues.push(data[r][c].value);
      }
      copyData += rowValues.join('\t') + '\n';
    }
    navigator.clipboard.writeText(copyData.trim());
  };

  // Handle cut
  const handleCut = () => {
    handleCopy();
    handleDelete();
  };

  // Handle paste
  const handlePaste = async () => {
    const clipboardText = await navigator.clipboard.readText();
    const rows = clipboardText.split('\n').map((row) => row.split('\t'));
    const startRow = selection.start.row;
    const startCol = selection.start.col;

    const newData = data.map((row, r) =>
      row.map((cell, c) => {
        const relativeRow = r - startRow;
        const relativeCol = c - startCol;
        if (relativeRow >= 0 && relativeRow < rows.length && relativeCol >= 0 && relativeCol < rows[0].length) {
          return { ...cell, value: rows[relativeRow][relativeCol] };
        } else {
          return cell;
        }
      })
    );

    dispatchHistory({ type: 'SET_DATA', data: newData });

    // Highlight pasted cells
    setSelection({
      start: { row: startRow, col: startCol },
      end: { row: startRow + rows.length - 1, col: startCol + rows[0].length - 1 },
    });
  };

  // Handle delete
  const handleDelete = () => {
    if (!selection.start || !selection.end) return;
    const startRow = Math.min(selection.start.row, selection.end.row);
    const endRow = Math.max(selection.start.row, selection.end.row);
    const startCol = Math.min(selection.start.col, selection.end.col);
    const endCol = Math.max(selection.start.col, selection.end.col);

    const newData = data.map((row, r) =>
      row.map((cell, c) => {
        if (r >= startRow && r <= endRow && c >= startCol && c <= endCol) {
          return { ...cell, value: '' };
        } else {
          return cell;
        }
      })
    );

    dispatchHistory({ type: 'SET_DATA', data: newData });
  };

  // Undo/Redo actions
  const undo = () => {
    dispatchHistory({ type: 'UNDO' });
  };

  const redo = () => {
    dispatchHistory({ type: 'REDO' });
  };

  // Resizing columns
  const handleColumnResize = (index, newWidth) => {
    setColWidths((prevWidths) => {
      const updatedWidths = [...prevWidths];
      updatedWidths[index] = newWidth;
      return updatedWidths;
    });
  };

  // Resizing rows
  const handleRowResize = (index, newHeight) => {
    setRowHeights((prevHeights) => {
      const updatedHeights = [...prevHeights];
      updatedHeights[index] = newHeight;
      return updatedHeights;
    });
  };

  // Adding new row
  const addRow = () => {
    const newRow = Array.from({ length: data[0].length }, () => ({ value: '' }));
    const newData = [...data, newRow];
    setRowHeights((prevHeights) => [...prevHeights, 30]); // default height 30px
    dispatchHistory({ type: 'SET_DATA', data: newData });
  };

  // Adding new column
  const addColumn = () => {
    const newData = data.map((row) => [...row, { value: '' }]);
    setColWidths((prevWidths) => [...prevWidths, 100]); // default width 100px
    dispatchHistory({ type: 'SET_DATA', data: newData });
  };

  // Export to CSV
  const exportCSV = () => {
    const csv = Papa.unparse(
      data.map((row) => row.map((cell) => cell.value))
    );
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'spreadsheet.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Import CSV
  const importCSV = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    Papa.parse(file, {
      complete: (results) => {
        const parsedData = results.data;
        const newData = parsedData.map((row) =>
          row.map((cellValue) => ({ value: cellValue }))
        );
        dispatchHistory({ type: 'SET_DATA', data: newData });
        setColWidths(() => {
          return Array.from({ length: newData[0].length }, () => 100);
        });
        setRowHeights(() => {
          return Array.from({ length: newData.length }, () => 30);
        });
      },
    });
  };

  // Context menu (right-click)
  const handleContextMenu = (e, rowIndex, colIndex) => {
    e.preventDefault();
    // Implement custom context menu here if needed
  };

  // Render the spreadsheet
  return (
    <div className="p-4">
      {/* Toolbar */}
      <div className="flex items-center space-x-2 mb-4">
        <button onClick={undo} className="px-2 py-1 bg-blue-500 text-white rounded">
          Undo
        </button>
        <button onClick={redo} className="px-2 py-1 bg-blue-500 text-white rounded">
          Redo
        </button>
        <button onClick={addRow} className="px-2 py-1 bg-green-500 text-white rounded">
          Add Row
        </button>
        <button onClick={addColumn} className="px-2 py-1 bg-green-500 text-white rounded">
          Add Column
        </button>
        <button onClick={exportCSV} className="px-2 py-1 bg-yellow-500 text-white rounded">
          Export CSV
        </button>
        <label className="px-2 py-1 bg-yellow-500 text-white rounded cursor-pointer">
          Import CSV
          <input type="file" accept=".csv" onChange={importCSV} className="hidden" />
        </label>
      </div>

      {/* Spreadsheet Grid */}
      <div
        className="overflow-auto border border-gray-300"
        style={{ maxHeight: '70vh', position: 'relative' }}
      >
        {/* Fixed header row */}
        <div
          className="sticky top-0 bg-gray-100 z-10"
          style={{
            display: 'grid',
            gridTemplateColumns: `50px ${colWidths.map((w) => `${w}px`).join(' ')}`,
          }}
        >
          {/* Empty cell for top-left corner */}
          <div className="border border-gray-300" style={{ width: 50, height: 30 }}></div>
          {data[0].map((_, colIndex) => (
            <ResizableBox
              key={colIndex}
              width={colWidths[colIndex]}
              height={30}
              axis="x"
              handle={
                <span
                  className="resizable-handle resizable-handle-x"
                  onClick={(e) => e.stopPropagation()}
                />
              }
              onResize={(e, { size }) => handleColumnResize(colIndex, size.width)}
              resizeHandles={['e']}
            >
              <div
                className="border border-gray-300 text-center font-bold"
                style={{ width: '100%', height: '100%', lineHeight: '30px' }}
              >
                {/* Column Header Label */}
                {String.fromCharCode(65 + colIndex)}
              </div>
            </ResizableBox>
          ))}
        </div>

        {/* Grid rows */}
        {data.map((row, rowIndex) => (
          <div
            key={rowIndex}
            className={`flex`}
            style={{
              display: 'grid',
              gridTemplateColumns: `50px ${colWidths.map((w) => `${w}px`).join(' ')}`,
            }}
          >
            {/* Fixed first column */}
            <div
              className="sticky left-0 bg-gray-100 border border-gray-300 text-center font-bold z-10"
              style={{
                width: 50,
                height: rowHeights[rowIndex],
                lineHeight: `${rowHeights[rowIndex]}px`,
              }}
            >
              {rowIndex + 1}
            </div>

            {row.map((cell, colIndex) => {
              const cellKey = `${rowIndex}-${colIndex}`;
              const isActive =
                activeCell && activeCell.row === rowIndex && activeCell.col === colIndex;
              const isEditing =
                editingCell && editingCell.row === rowIndex && editingCell.col === colIndex;
              const isSelected = isCellSelected(rowIndex, colIndex);

              return (
                <ResizableBox
                  key={colIndex}
                  width={colWidths[colIndex]}
                  height={rowHeights[rowIndex]}
                  axis="none"
                  resizeHandles={[]}
                  handle={
                    <span
                      className="resizable-handle resizable-handle-y"
                      onClick={(e) => e.stopPropagation()}
                    />
                  }
                >
                  <div
                    ref={(el) => (cellRefs.current[cellKey] = el)}
                    tabIndex={0}
                    className={`border border-gray-300 ${
                      isSelected ? 'bg-blue-100' : 'bg-white'
                    } ${isActive ? 'border-2 border-blue-500' : ''} ${
                      isEditing ? 'bg-yellow-100' : ''
                    } hover:bg-gray-100 focus:outline-none`}
                    style={{
                      width: '100%',
                      height: '100%',
                      padding: '2px',
                    }}
                    onKeyDown={(e) => handleKeyDown(e, rowIndex, colIndex)}
                    onClick={(e) => handleCellClick(rowIndex, colIndex, e)}
                    onDoubleClick={() => handleDoubleClick(rowIndex, colIndex)}
                    onContextMenu={(e) => handleContextMenu(e, rowIndex, colIndex)}
                  >
                    {isEditing ? (
                      <input
                        type="text"
                        value={cell.value}
                        onChange={(e) => handleChange(e, rowIndex, colIndex)}
                        onBlur={() => {
                          setEditingCell(null);
                          // Ensure to update the cell value on blur
                          handleChange({ target: { value: cell.value } }, rowIndex, colIndex);
                        }}
                        autoFocus
                        className="w-full h-full p-1"
                      />
                    ) : (
                      <div className="w-full h-full p-1">{cell.value}</div>
                    )}
                  </div>
                </ResizableBox>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
};

export default Spreadsheet;
